import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Building2, Users, BookOpen, UserPlus, Trash2, Loader2, Mail, Phone, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export const OrganizationTab = () => {
  const { user, api } = useAuth();
  const { isDark } = useTheme();
  const orgId = user?.organization_id;
  const isOrgAdmin = user?.is_org_admin;

  const [org, setOrg] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [orgBookings, setOrgBookings] = useState([]);
  const [activeView, setActiveView] = useState('employees');
  const [loading, setLoading] = useState(true);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [addingEmployee, setAddingEmployee] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ name: '', email: '', phone: '', password: 'Clayworks@123' });

  const fetchData = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [orgRes, empRes, bookRes] = await Promise.all([
        api.get(`/organizations/${orgId}`),
        api.get(`/organizations/${orgId}/employees`),
        api.get(`/organizations/${orgId}/bookings`),
      ]);
      setOrg(orgRes.data);
      setEmployees(empRes.data);
      setOrgBookings(bookRes.data);
    } catch (err) {
      console.error('Failed to fetch org data:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddEmployee = async () => {
    if (!newEmployee.name || !newEmployee.email) {
      toast.error('Name and email are required');
      return;
    }
    setAddingEmployee(true);
    try {
      const res = await api.post(`/organizations/${orgId}/employees`, newEmployee);
      toast.success(res.data.message);
      setShowAddEmployee(false);
      setNewEmployee({ name: '', email: '', phone: '', password: 'Clayworks@123' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add employee');
    } finally {
      setAddingEmployee(false);
    }
  };

  const handleRemoveEmployee = async (empId, empName) => {
    if (!window.confirm(`Remove ${empName} from the organization?`)) return;
    try {
      await api.delete(`/organizations/${orgId}/employees/${empId}`);
      toast.success(`${empName} removed from organization`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove employee');
    }
  };

  if (!orgId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No Organization</h3>
        <p className="text-muted-foreground text-sm max-w-sm">
          You're not part of any organization. Contact your company admin or register a business account.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const viewTabs = [
    { id: 'employees', label: 'Team', icon: Users, count: employees.length },
    { id: 'bookings', label: 'Bookings', icon: BookOpen, count: orgBookings.length },
  ];

  return (
    <div className="space-y-6">
      {/* Org Info Card */}
      <Card className={isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                <Building2 className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-bold" data-testid="org-name">{org?.name}</h3>
                <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                  {org?.gstin && <span>GSTIN: {org.gstin}</span>}
                  <span>{employees.length} member{employees.length !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>
            {isOrgAdmin && (
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-primary/20 text-primary' : 'bg-primary/10 text-primary'}`}>
                Admin
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1">
        {viewTabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              data-testid={`org-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeView === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : isDark ? 'text-muted-foreground hover:bg-[#1e2128]' : 'text-muted-foreground hover:bg-[#efe9da]'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                activeView === tab.id
                  ? 'bg-primary-foreground/20'
                  : isDark ? 'bg-[#1e2128]' : 'bg-[#e5dfd0]'
              }`}>{tab.count}</span>
            </button>
          );
        })}
        <div className="flex-1" />
        {isOrgAdmin && activeView === 'employees' && (
          <Button
            onClick={() => setShowAddEmployee(true)}
            size="sm"
            className="bg-primary hover:bg-primary/90"
            data-testid="org-add-employee-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Employee
          </Button>
        )}
      </div>

      {/* Employees View */}
      {activeView === 'employees' && (
        <div className="space-y-3">
          {employees.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No employees yet. Add your first team member.</p>
            </div>
          ) : (
            employees.map(emp => (
              <Card key={emp.id} className={isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                      isDark ? 'bg-[#1e2128] text-foreground' : 'bg-[#efe9da] text-foreground'
                    }`}>
                      {emp.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{emp.name}</span>
                        {emp.is_org_admin && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">Admin</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{emp.email}</span>
                        {emp.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{emp.phone}</span>}
                      </div>
                    </div>
                  </div>
                  {isOrgAdmin && !emp.is_org_admin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveEmployee(emp.id, emp.name)}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      data-testid={`org-remove-employee-${emp.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Org Bookings View */}
      {activeView === 'bookings' && (
        <div className="space-y-3">
          {orgBookings.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No bookings yet from your organization.</p>
            </div>
          ) : (
            orgBookings.map(booking => (
              <Card key={booking.id} className={isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{booking.bookable_name || 'Workspace'}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{booking.booking_date}</span>
                        <span>{booking.start_time} - {booking.end_time}</span>
                        <span>by {booking.user_name}</span>
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      booking.status === 'confirmed' ? 'bg-green-500/10 text-green-500' :
                      booking.status === 'pending' ? 'bg-yellow-500/10 text-yellow-500' :
                      booking.status === 'cancelled' ? 'bg-red-500/10 text-red-500' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Add Employee Dialog */}
      <Dialog open={showAddEmployee} onOpenChange={setShowAddEmployee}>
        <DialogContent className={isDark ? 'bg-[#13161a] border-[#1e2128]' : ''}>
          <DialogHeader>
            <DialogTitle>Add Employee</DialogTitle>
            <DialogDescription>Add a team member to your organization. They'll receive login credentials.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Name *</Label>
              <Input
                placeholder="Employee name"
                value={newEmployee.name}
                onChange={(e) => setNewEmployee(p => ({ ...p, name: e.target.value }))}
                data-testid="org-employee-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Email *</Label>
              <Input
                type="email"
                placeholder="employee@company.com"
                value={newEmployee.email}
                onChange={(e) => setNewEmployee(p => ({ ...p, email: e.target.value }))}
                data-testid="org-employee-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Phone</Label>
              <Input
                type="tel"
                placeholder="+919876543210"
                value={newEmployee.phone}
                onChange={(e) => setNewEmployee(p => ({ ...p, phone: e.target.value }))}
                data-testid="org-employee-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Initial password</Label>
              <Input
                value={newEmployee.password}
                onChange={(e) => setNewEmployee(p => ({ ...p, password: e.target.value }))}
                data-testid="org-employee-password-input"
              />
              <p className="text-xs text-muted-foreground">Employee will be prompted to change this on first login.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddEmployee(false)}>Cancel</Button>
            <Button
              onClick={handleAddEmployee}
              disabled={addingEmployee}
              className="bg-primary hover:bg-primary/90"
              data-testid="org-employee-submit-btn"
            >
              {addingEmployee ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Employee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
